import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { fontSize, desktopMinWidth } from '../mixins';
import { StyledBtn, StyledBtnWrapper } from '../features/checkout/CheckoutScreen'

const Button = styled.button`
	width: 100%;
	height: 56px;
	min-height: 56px;
	max-width: ${({ customWidth }) => customWidth || '327px'};
	background-color: ${({ customColor }) => customColor || '#F58A3C'};
	border-radius: 12px;
	border: none;
	padding: 20px 0;
	margin: ${({ customMargin }) => customMargin || '24px 0 0'};
	font-weight: 700;
	${fontSize(16)};
	line-height: 1;
	color: #ffffff;
	position: relative;
	z-index: ${({ zIndex }) => zIndex || 'initial'};
	opacity: ${({ disabled }) => (disabled ? '0.5' : '1')};
	transition: background-color 0.3s;
	${({ customStyles }) => customStyles};

	&:focus {
		outline: none;
	}

	${({ customColor, disabled }) =>
		!customColor &&
		!disabled &&
		`
    @media screen and (min-width: ${desktopMinWidth}px) {
      &:hover {
        background-color: #F99B57;
      }
    }

    &:active {
      background-color: #EA7825;
    }
  `}
`;

const StickyWrapper = styled.div`
	--bottom-gap: 25px;

	position: ${({ position }) => position || 'fixed'};
	left: auto;
	bottom: ${({ bottomPosition }) => bottomPosition};
	width: 100%;
	z-index: ${({ zIndex }) => zIndex || 'initial'};
	display: flex;
	flex-direction: column;
	align-items: center;
	@media screen and (min-width: 450px) {
		position: sticky;
		top: unset;
		align-items: ${({ alignStickyBtn }) => alignStickyBtn || 'center'};
	}

	&:before {
		content: '';
		display: block;
		width: 100vw;
		height: ${({ customBeforeHeight, bottomPosition }) =>
			customBeforeHeight
				? `${customBeforeHeight}` || '0px'
				: `calc(100% + var(--bottom-gap) + ${bottomPosition})`};
		position: absolute;
		left: calc(-50vw + 50%);
		top: -10px;
		background: ${({ customBeforeBg }) =>
			customBeforeBg ||
			`linear-gradient(0deg,#fff 0%,#fff 75%,rgba(255,255,255,0.5) 85%,rgba(255,255,255,0.35) 95%,rgba(255,255,255,0) 100%)`};
		pointer-events: none;
	}
`;

const StyledBtnWelcomeWrapper = styled(StyledBtnWrapper)`
	margin-top: 10px;
	color: rgb(175, 175, 175);
`;

const StyledDisclaimer = styled.div`
  font-size: 12px;
  color: rgb(175, 175, 175);
  text-align: center;
  padding: 10px 10px 0;

  span {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const UIButton = ({
	onClick,
	children,
	customText,
	disabled,
	customBeforeBg,
	customBeforeHeight = true,
	customColor,
	customWidth,
	zIndex,
	customId,
	position,
	displayStickyBtn = true,
	bottomPosition,
	stickyProps,
	alignStickyBtn,
	customMargin,
	customStyles,
	withHint,
}) => {
	const { t } = useTranslation();
	const customInner = customText || t('continue');
	const onClickFunc = () => {
		if (disabled) return;
		onClick();
	};

	const onLinkClick = (url) => window.open(url);

	const btn = (
		<>
			<Button
				onClick={onClickFunc}
				disabled={disabled}
				customBeforeBg={customBeforeBg}
				customBeforeHeight={customBeforeHeight}
				customColor={customColor}
				customMargin={customMargin}
				customWidth={customWidth}
				zIndex={zIndex}
				id={customId || 'continue-button'}
				customStyles={customStyles}
			>
				{children || t(customInner)}
			</Button>
			{withHint && (
				<>
					<StyledDisclaimer
						dangerouslySetInnerHTML={{ __html: t('welcome_disclaimer') }}
						onClick={(e) => {
							if (e.target.id === 'terms') {
								onLinkClick('https://familo.net/en/assets/Terms_and_Conditions_Familonet_info.pdf');
							}
						}}
					/>
					<StyledBtnWelcomeWrapper>
						<StyledBtn
							onClick={() =>
								onLinkClick(
									'https://familo.net/en/assets/Terms_and_Conditions_Familonet_info.pdf'
								)
							}
							id={'link-to-terms'}
						>
							{t('terms')}
						</StyledBtn>
						<StyledBtn
							onClick={() => onLinkClick('https://terms.familo.net/privacy-info')}
							id={'link-to-privacy'}
						>
							{t('privacy')}
						</StyledBtn>
					</StyledBtnWelcomeWrapper>
				</>
			)}
		</>
	);

	const stickyContent = (
		<StickyWrapper
			position={position}
			bottomPosition={bottomPosition}
			zIndex={zIndex}
			customBeforeBg={customBeforeBg}
			customBeforeHeight={customBeforeHeight}
			alignStickyBtn={alignStickyBtn}
			{...stickyProps}
		>
			{btn}
		</StickyWrapper>
	);

	return displayStickyBtn && (bottomPosition || stickyProps) ? (
		<>{stickyContent}</>
	) : (
		btn
	);
};

export default UIButton;
